import { paragon } from "@useparagon/connect";
import Button from "components/atoms/Button/Button";
import { Card } from "components/atoms/Card";
import { Empty } from "components/atoms/Empty";
import Icon from "components/atoms/Icon/Icon";
import Skeleton from "components/atoms/layout/Skeleton/Skeleton";
import Loading from "components/atoms/Loading/Loading";
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getBillingPortalUrl } from "store/slices/card.slice";
import constants from "utils/constants";
import { SegmentEventENUM } from "utils/enums/segmentENUM";
import { emptyMessages } from "utils/message";
import { cleanObject } from "utils/object";

export const ParagonIntegrations = ({
  changeLoading,
}: {
  changeLoading?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { data: user } = useAppSelector((state) => state.user);
  const { data: authToken, isSuccess: authIsSuccess } = useAppSelector(
    (state) => state.crm.paragonAuth
  );
  const [isLoading, setIsLoading] = useState(true);
  const [userParagon, setUserParagon] = useState<any>();
  const { billingPortalIsLoading } = useAppSelector((state) => state.cards);
  const handleEnhanceYourExperience = useCallback(async () => {
    const billingData = await dispatch(getBillingPortalUrl());
    const url = billingData.payload.data;
    if (!url) {
      toast.error("Something went wrong. Please try again later.");
      return;
    }
    window.open(url, "_blank");
  }, [dispatch]);

  const sendTrack = (integrationName: string) => {
    const cleanedFilters = cleanObject({
      crmClicked: integrationName,
      firstname: user.firstName,
      lastname: user.lastName,
      email: user.email,
    });
    window.analytics.track(
      SegmentEventENUM.INTEGRATION_EXPLORED,
      cleanedFilters
    );
  };
  useEffect(() => {
    if (!authIsSuccess) return;
    setIsLoading(true);
    paragon
      .authenticate(authToken.projectId || "", authToken.authToken || "")
      .then(() => {
        setUserParagon(paragon.getUser());
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [authIsSuccess]);
  return (
    <Card
      title="Integrations"
      subtitle="Send hot leads right to your favorite tools"
      icon="category"
    >
      <div>
        {!isLoading && !authToken.isClientAuthorized && (
          <EnhanceYourExperience
            handlePress={handleEnhanceYourExperience}
            isLoading={billingPortalIsLoading}
          />
        )}
        {isLoading &&
          changeLoading &&
          !paragon.getIntegrationMetadata().length && (
            <div className="col-span-12 grid grid-cols-12 justify-center items-center sm:grid-cols-12 gap-6">
              <div className="flex justify-center items-center col-span-12 sm:col-span-12 gap-2">
                <Loading height="auto" spinnerSize={38} />
              </div>
            </div>
          )}
        {isLoading &&
          !changeLoading &&
          !paragon.getIntegrationMetadata().length && (
            <div className="col-span-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          )}
        {paragon && paragon.getIntegrationMetadata().length > 0 ? (
          <div className="col-span-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
            <ZapierLink handlePress={() => sendTrack("Zapier")} />
            {paragon.getIntegrationMetadata().map((integration) => {
              const isEnabled =
                userParagon?.integrations?.[integration.type]?.enabled;

              return (
                <div
                  key={integration.type}
                  className={`border border-solid border-gray-700 rounded-xl p-4 bg-paper-400 flex flex-col items-start gap-3 ${
                    !authToken.isUserAuthorized
                      ? ``
                      : `cursor-pointer hover:bg-gray-700 transition-all duration-300`
                  }`}
                  onClick={() => {
                    sendTrack(integration.name);
                    if (!authToken.isUserAuthorized) return;
                    paragon.connect(integration.type, {
                      onSuccess: () => {
                        setUserParagon(paragon.getUser());
                      },
                      onError: (e) => {},
                    });
                  }}
                >
                  <img src={integration.icon} className="!h-[70px]" />
                  <h3 className="font-bold text-[19px]">{integration.name}</h3>
                  <div
                    className={`flex items-center gap-1 font-bold text-[14px] ${
                      !isEnabled ? "text-gray-500" : "text-green-500"
                    }`}
                  >
                    {isEnabled ? (
                      <Icon
                        name="check-circle"
                        size={15}
                        className="text-green-500"
                      />
                    ) : (
                      <Icon
                        name="refresh"
                        size={15}
                        className="text-gray-500"
                      />
                    )}
                    {isEnabled ? "Connected" : "Not connected"}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
        {!isLoading &&
          authToken.isClientAuthorized &&
          !paragon.getIntegrationMetadata().length && (
            <div className="col-span-12 w-full justify-center">
              <Empty
                type="no-data"
                title="No Integrations Found"
                message={emptyMessages.integrations}
              />
            </div>
          )}
      </div>
    </Card>
  );
};
//TODO poderia separar
const SkeletonCard = () => {
  return (
    <div className="border border-solid border-gray-700 rounded-xl p-4 bg-paper-400 flex flex-col items-start gap-3">
      <Skeleton width={50} height={50} />
      <Skeleton width={120} height={20} />
      <div className="flex gap-2 mt-auto">
        <Skeleton width={10} height={10} />
        <Skeleton width={140} height={10} />
      </div>
    </div>
  );
};
//TODO poderia separar
const EnhanceYourExperience = ({
  handlePress,
  isLoading,
}: {
  handlePress: () => void;
  isLoading: boolean;
}) => {
  return (
    <div className="w-full flex justify-center items-center py-10">
      <div className="max-w-md w-full bg-gray-100 rounded-lg border border-gray-700 p-6 shadow-sm flex flex-col items-start gap-4">
        <h1 className="font-bold text-lg text-white-500">
          Enhance Your Experience
        </h1>
        <p className="text-sm leading-relaxed text-gray-300">
          Your current plan doesn't include access to most of the integrations.
          To unlock it and enjoy even more benefits, consider upgrading your
          subscription. More info, contact our team by the email{" "}
          <a
            className="!text-primary-300 underline font-semibold"
            href={`mailto:${constants.COMPANY_1_EMAIL_SUPPORT}`}
          >
            {constants.COMPANY_1_EMAIL_SUPPORT}
          </a>
          .
        </p>
        <Button onClick={handlePress} iconName="membership">
          {isLoading ? "Redirecting..." : "Upgrade Now"}
        </Button>
      </div>
    </div>
  );
};

const ZapierLink = ({ handlePress }: { handlePress: () => void }) => {
  return (
    <a
      onClick={handlePress}
      href="https://zapier.com/apps/identity-matrix/integrations"
      target="_blank"
      rel="noreferrer"
      className="border border-solid border-gray-700 rounded-xl p-4 bg-paper-400 flex flex-col items-start gap-3 cursor-pointer hover:bg-gray-700 transition-all duration-300 h-full"
    >
      <Icon name="zapier" size={70} color="#ff4f00" />
      <h3 className="font-bold text-[19px]">Zapier</h3>
      <div className="flex items-center gap-1 font-bold text-[14px] text-green-500">
        <Icon name="check-circle" size={15} className="text-green-500" />
        Available
      </div>
    </a>
  );
};
